import React, { useContext, useMemo, useState } from "react";
import { TranslationsContext } from "../../../utils/TranslationsContext";
import AccreditationInput from "../../AccreditationInput/AccreditationInput";
import { apiRoutes } from "../../../utils/apiRoutes";
import { trimValues } from "../../../utils/trimValues";
import { updateData, createData, deleteData } from "../../../utils/methods";
import { useAlert } from "../../../utils/AlertContext";
import { useNavigate } from "react-router-dom";
import EditModalShell from "./EditModalShell";
import ConfirmationModal from "./ConfirmationModal";
import useEnterKeySubmit from "../../../hooks/useEnterKeySubmit";
import { diffChangedFields } from "../../../utils/diffChangedFields";
import { buildEmailPreviewHtml } from "../../../constants/emailDefaultDesign";

const AddTemplate = ({
  onCloseClick,
  editing,
  object,
  handleAddData,
  handleUpdateData,
  handleDeleteData,
}) => {
  const { t } = useContext(TranslationsContext);
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [templateObject, setTemplateObject] = useState(
    object
      ? object
      : {
          naziv: "",
          subject: "",
          textBody: "",
        },
  );
  const [errors, setErrors] = useState({});

  const previewHtml = useMemo(
    () => buildEmailPreviewHtml(templateObject.textBody),
    [templateObject.textBody],
  );

  const setInputValue = (key, value) => {
    setTemplateObject((prevValues) => ({
      ...prevValues,
      [key]: value,
    }));
  };

  const validateAllFields = () => {
    const fieldsToValidate = ["naziv", "subject", "textBody"];
    let hasError = false;

    fieldsToValidate.forEach((field) => {
      const value = templateObject[field];
      if (!value) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: true,
        }));
        hasError = true;
      }
    });

    return !hasError;
  };

  const handleSaveClick = async () => {
    setErrors({});
    const isValid = validateAllFields();
    if (!isValid) return;

    const id = object.id;
    const route = apiRoutes.updateTemplate.replace(":templateId", id);

    const basePayload = { ...templateObject };
    delete basePayload.id;

    const old =
      templateObject.old_template || templateObject.old_group || object;

    const objekatPromena = diffChangedFields(basePayload, old, [
      "old_group",
      "old_template",
    ]);
    const sendData = trimValues(objekatPromena);

    setIsLoading(true);

    try {
      const result = await updateData(route, sendData, showAlert, navigate, t);
      if (!result) return;

      if (handleUpdateData) {
        handleUpdateData({ ...sendData, id: result.data?.id || id });
      }

      showAlert(t("SUCCESSFULLY_SAVED"), "success");
      onCloseClick();
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = async () => {
    setErrors({});
    const isValid = validateAllFields();
    if (!isValid) return;

    setIsLoading(true);

    const sendData = trimValues(templateObject);

    try {
      const result = await createData(
        apiRoutes.EmailTemplate,
        sendData,
        showAlert,
        navigate,
        t,
      );

      if (!result) return;

      if (handleAddData) {
        handleAddData({ ...sendData, id: result.data?.id });
      }

      showAlert(t("SUCCESSFULLY_SAVED"), "success");
      onCloseClick();
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    const route = apiRoutes.deleteTemplate.replace(":templateId", object.id);
    setIsLoading(true);
    try {
      const result = await deleteData(route, null, showAlert, navigate, t);
      if (!result) return;

      handleDeleteData?.(result.data?.id || object.id);
      showAlert(t("SUCCESSFULLY_DELETED"), "success");
      onCloseClick();
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
      setIsConfirmDeleteOpen(false);
    }
  };

  useEnterKeySubmit(editing ? handleSaveClick : handleAddClick);

  return (
    <>
    <EditModalShell
      isLoading={isLoading}
      t={t}
      headerInfo={
        <p className="inria-sans-400 font-24 modal-title">
          {editing ? t("EDIT_TEMPLATE_TITLE") : t("ADD_TEMPLATE_TITLE")}
        </p>
      }
      onSave={editing ? handleSaveClick : handleAddClick}
      onClose={onCloseClick}
      onDelete={editing ? () => setIsConfirmDeleteOpen(true) : undefined}
      sidePanel={
        <div className="flex-direction-column gap-8 width-100 side-panel-card">
          <label className="width-100 font-12 inria-sans-700">
            {t("EMAIL_TEMPLATE_PREVIEW")}
          </label>
          <iframe
            title="email-preview"
            srcDoc={previewHtml}
            className="email-preview-frame"
            sandbox=""
          />
        </div>
      }
    >
      <div className="flex-direction-column gap-24 width-100 modal-content">
        <AccreditationInput
          className="modal-input"
          label={t("NAME_1")}
          setInputValue={(value) => setInputValue("naziv", value)}
          value={templateObject.naziv}
          error={errors?.naziv}
          required={true}
        />

        <AccreditationInput
          className="modal-input"
          label={t("SUBJECT")}
          setInputValue={(value) => setInputValue("subject", value)}
          value={templateObject.subject}
          error={errors?.subject}
          required={true}
        />

        <AccreditationInput
          type="text-area"
          label={t("EMAIL_TEMPLATE")}
          setInputValue={(value) => setInputValue("textBody", value)}
          value={templateObject.textBody}
          error={errors?.textBody}
          required={true}
          className="email-textarea template-textarea"
          email
        />
      </div>
    </EditModalShell>
    {isConfirmDeleteOpen && (
      <ConfirmationModal
        handleDeclineClick={() => setIsConfirmDeleteOpen(false)}
        handleConfirmClick={() => handleDeleteClick()}
        term={templateObject.naziv}
      />
    )}
    </>
  );
};

export default AddTemplate;
